import { useMemo } from 'react';
import { useUser } from 'contexts/user';
import { getTenantById } from '@tenants';
import { useConsentLabels } from 'hooks/use-consent-labels';
import { createConsentService } from 'services/consent-capture-service';
import { useTranslation } from 'next-i18next';
import { Namespaces, LOCALE_COMMON_PATHS } from 'types/locales';
import { useTenantId } from './use-tenant-id';

/**
 * Hook that provides a consent capture service instance and consent labels.
 * 
 * This is the "plug-in point" for the consent widget.
 * The UI just uses this hook and doesn't need to know about
 * tenant configuration, user location, or consent logic.
 * 
 * @example
 * ```tsx
 * const { service, labels } = useConsentService();
 * 
 * // Check if disclaimers should be shown
 * const showDisclaimers = service.shouldShow();
 * 
 * // Capture consent (service handles everything)
 * await service.capture({
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   email: 'john@example.com',
 *   phone: '555-1234'
 * });
 * ```
 */
export function useConsentService(buttonText?: string) {
  const tenantId = useTenantId();
  const tenant = getTenantById(tenantId);
  const { userLocation } = useUser();
  const { t } = useTranslation(Namespaces.COMMON);
  const defaultButtonText = t(LOCALE_COMMON_PATHS.BUTTON_CONTINUE, { defaultValue: 'Continue' });
  const { fubConsentDisclaimerText, privacyPolicyConsentLabel, marketingCommunicationsConsentLabel } = useConsentLabels(buttonText || defaultButtonText);

  const service = useMemo(
    () => createConsentService(
      tenant,
      userLocation,
      fubConsentDisclaimerText
    ),
    [tenant, userLocation, fubConsentDisclaimerText]
  );

  return {
    service,
    labels: {
      privacyPolicy: privacyPolicyConsentLabel,
      marketingCommunications: marketingCommunicationsConsentLabel,
    },
  };
}

